import { AfterViewInit, Component, EventEmitter, Inject, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { ESRI } from '../../esri-loader-util';
import { InputConverter } from '../../inputConverter';
import { IAuthenticationService } from '../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../services/interfaces/IUserSettingsService';
import { LogLevel } from '../../types';
import { BaseComponent } from '../base.component';

import ExtentConfiguration, { ExtentConfigurationArrayConverter, ExtentReturnAsyncFunction, ExtentReturnFunction, IExtentConfiguration } from './extent.config';

let Extent: __esri.ExtentConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-extent',
    styleUrls: ['../sharedStyles/base.scss', './extent.component.scss'],
    templateUrl: './extent.component.html'
})
export class ExtentComponent extends BaseComponent implements AfterViewInit {
    @Output() readonly extentChanged: EventEmitter<__esri.Extent> = new EventEmitter();

    @Input() private mapViewRef: NonNullable<__esri.MapView>;
    @Input() showLabels = false;
    @Input() vertical = true;
    @Input() animateChange = true;
    @Input() includeUserDefault = true;
    @Input() appName: string;

    @Input()
    @InputConverter(ExtentConfigurationArrayConverter)
    extentConfigs: ExtentConfiguration[] = [];

    userEmail: string;

    constructor(
        _vcr: ViewContainerRef,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        @Inject('UserSettingsService') public UserSettingsService: IUserSettingsService,
        @Inject('authenticationService') private readonly authService: IAuthenticationService
    ) {
        super(_vcr);
        this.authService.GetCurrentUser().subscribe(user => {
            if (user && user.id) {
                this.userEmail = user.emailAddress;
            }
        });
    }

    async ngAfterViewInit() {
        this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        this.setupUserDefault();
        this.isReady();
    }

    @Input()
    public addExtentConfiguration(extentConfig: IExtentConfiguration) {
        this.extentConfigs.push(new ExtentConfiguration(extentConfig));
    }

    private async setupUserDefault() {
        if (this.includeUserDefault) {
            this.setDefaultExtent(true);
            try {
                const userSettingsComponent = await this.getClosestAncestor('mc-user-settings');
                userSettingsComponent.activeChange.subscribe((active: boolean) => {
                    if (!active) {
                        this.setDefaultExtent();
                    }
                });
            }
            catch (e) { }
            // TODO: Check for existing configuration and try to match color and other properties?
            // TODO: If no user default exists, get original extent from map?
        }
    }

    private setDefaultExtent(firstLoad: boolean = false) {
        this.UserSettingsService.GetSettingValue('userSettings', this.appName, this.userEmail).subscribe(async result => {
            Extent = await ESRI.Extent;
            const existingConfig = this.extentConfigs.find(extentConfig => extentConfig.label === 'Default Extent');
            if (result && result.extent && result.extent.default) {
                const defaultExtent = Extent.fromJSON(result.extent.default);
                if (existingConfig) {
                    existingConfig.extent = defaultExtent;
                } else {
                    const extentConfig = new ExtentConfiguration({
                        extent: defaultExtent,
                        icon: 'home',
                        label: 'Default Extent'
                    });
                    this.extentConfigs.unshift(extentConfig);
                    if (firstLoad) {
                        this.changeMapExtent(extentConfig);
                    }
                }
            } else if (existingConfig) {
                this.extentConfigs.splice(this.extentConfigs.indexOf(existingConfig), 1);
            }
        });
    }

    @Input()
    public executeExtentChange = (extentConfig: ExtentConfiguration): void => {
        this.changeMapExtent(extentConfig);
    }

    private async changeMapExtent(extentConfig: ExtentConfiguration) {
        let extent: __esri.Extent | __esri.GoToTarget2D | number[];

        if (extentConfig.extentType === 'async-func') {
            extent = await (<ExtentReturnAsyncFunction>extentConfig.extent)(this.mapViewRef);
        } else if (extentConfig.extentType === 'func') {
            extent = (<ExtentReturnFunction>extentConfig.extent)(this.mapViewRef);
        } else {
            extent = extentConfig.extent as __esri.Extent | __esri.GoToTarget2D | number[];
        }

        await this.mapViewRef.goTo(extent, { animate: this.animateChange });
        this.extentChanged.emit(this.mapViewRef.extent);
        this.LoggingService.Log('Extent Changed', this.mapViewRef.extent, LogLevel.Trace);
    }
}
